import { useCallback } from 'react'

import { HotelAvailabilityCalendar } from '@/components/hotel-availability-calendar'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'

import { EmployeesManagement } from './components/employees-management'
import { PricingPeriodsTab } from './components/pricing-periods-tab'
import { HolidayDialog } from './disponibilidad/components/dialogs/holiday-dialog'
import { ManageDatesDialog } from './disponibilidad/components/dialogs/manage-dates-dialog'
import { useAvailabilityDialogs } from './disponibilidad/hooks/use-availability-dialogs'
import { useAvailabilityOperations } from './disponibilidad/hooks/use-availability-operations'
import { DateRange } from './disponibilidad/types'

export default function SetupPage() {
    const {
        isBlockDateDialogOpen,
        isHolidayDialogOpen,
        selectedRange,
        handleDialogOpenChange,
        handleHolidayDialogOpenChange,
        openHolidayDialog,
        setSelectedRange,
    } = useAvailabilityDialogs()

    const {
        handleBlockDates,
        handleAddHolidays,
        handleAddHighSeason,
        handleRemoveSpecialConditions,
        getSelectedDatesStatus,
    } = useAvailabilityOperations()

    const handleDateRangeSelect = useCallback(
        (range: DateRange) => {
            setSelectedRange(range)
            if (range.from && range.to) {
                handleDialogOpenChange(true)
            }
        },
        [setSelectedRange, handleDialogOpenChange],
    )

    const dateStatus = selectedRange.from && selectedRange.to ? getSelectedDatesStatus(selectedRange) : null
    const hasSpecialConditions = dateStatus
        ? dateStatus.hasBlocked || dateStatus.hasHolidays || dateStatus.hasHighSeason
        : false

    return (
        <div className='container mx-auto p-6 pb-24'>
            <h1 className='mb-6 text-3xl font-bold'>Configuración</h1>

            <Tabs defaultValue='general' className='space-y-6'>
                <TabsList className='grid w-full grid-cols-3 gap-4 bg-transparent p-0'>
                    <TabsTrigger
                        value='general'
                        className='relative flex items-center justify-center gap-2 border bg-white shadow-sm hover:bg-gray-50/80 data-[state=active]:border-[#4B6BFB] data-[state=active]:bg-[#4B6BFB] data-[state=active]:text-white'
                    >
                        Configuración General
                    </TabsTrigger>
                    <TabsTrigger
                        value='calendar'
                        className='relative flex items-center justify-center gap-2 border bg-white shadow-sm hover:bg-gray-50/80 data-[state=active]:border-[#4B6BFB] data-[state=active]:bg-[#4B6BFB] data-[state=active]:text-white'
                    >
                        Calendario
                    </TabsTrigger>
                    <TabsTrigger
                        value='employees'
                        className='relative flex items-center justify-center gap-2 border bg-white shadow-sm hover:bg-gray-50/80 data-[state=active]:border-[#4B6BFB] data-[state=active]:bg-[#4B6BFB] data-[state=active]:text-white'
                    >
                        Empleados
                    </TabsTrigger>
                </TabsList>

                <TabsContent value='general'>
                    <PricingPeriodsTab />
                </TabsContent>

                <TabsContent value='calendar'>
                    <div className='space-y-6'>
                        <HotelAvailabilityCalendar onDateSelect={handleDateRangeSelect} />

                        <div className='flex flex-wrap justify-center gap-4'>
                            <Badge variant='outline' className='py-1'>
                                <div className='mr-2 h-3 w-3 rounded-full bg-red-500' />
                                Bloqueado
                            </Badge>
                            <Badge variant='outline' className='py-1'>
                                <div className='mr-2 h-3 w-3 rounded-full bg-amber-500' />
                                Festivo
                            </Badge>
                            <Badge variant='outline' className='py-1'>
                                <div className='mr-2 h-3 w-3 rounded-full bg-green-500' />
                                Temporada Alta
                            </Badge>
                        </div>

                        {selectedRange.from && selectedRange.to && (
                            <div className='space-y-4'>
                                <div className='flex flex-wrap gap-4'>
                                    <Button
                                        variant='outline'
                                        onClick={() => handleDialogOpenChange(true)}
                                        disabled={dateStatus?.hasBlocked}
                                    >
                                        Bloquear fechas
                                    </Button>
                                    <Button
                                        variant='outline'
                                        onClick={openHolidayDialog}
                                        disabled={dateStatus?.hasHolidays}
                                    >
                                        Marcar como festivo
                                    </Button>
                                    <Button
                                        variant='outline'
                                        onClick={() => handleAddHighSeason(selectedRange)}
                                        disabled={dateStatus?.hasHighSeason}
                                    >
                                        Marcar como temporada alta
                                    </Button>
                                    {hasSpecialConditions && (
                                        <Button
                                            variant='destructive'
                                            onClick={() => handleRemoveSpecialConditions(selectedRange)}
                                        >
                                            Eliminar condiciones especiales
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value='employees'>
                    <EmployeesManagement />
                </TabsContent>
            </Tabs>

            <ManageDatesDialog
                open={isBlockDateDialogOpen}
                onOpenChange={handleDialogOpenChange}
                selectedRange={selectedRange}
                onBlockDates={() => handleBlockDates(selectedRange)}
                onOpenHolidayDialog={openHolidayDialog}
                onHighSeasonClick={() => handleAddHighSeason(selectedRange)}
                onRemoveSpecialConditions={() => handleRemoveSpecialConditions(selectedRange)}
                hasSpecialConditions={hasSpecialConditions}
            />

            <HolidayDialog
                open={isHolidayDialogOpen}
                onOpenChange={handleHolidayDialogOpenChange}
                selectedRange={selectedRange}
                onSubmit={holidayName => handleAddHolidays(selectedRange, holidayName)}
            />
        </div>
    )
}
